(() => {
  const SENTINEL_SELECTOR = 'shefa-infinite-scroll[data-shefa-infinite-scroll]';
  const SEARCH_GRID_SELECTOR = '#search-list-id';

  function getNextUrlFromScope(scope) {
    if (!scope) return '';

    const sentinel = scope.querySelector?.(SENTINEL_SELECTOR);
    if (sentinel?.dataset?.nextUrl) return sentinel.dataset.nextUrl;

    // Loka's native pagination uses --prev for the visual "next" arrow.
    const nativeNext = scope.querySelector?.(
      '.pagination-wrapper a.pagination__item--prev[href], .pagination-wrapper a[rel="next"][href]'
    );
    if (nativeNext) return nativeNext.getAttribute('href') || nativeNext.href || '';

    const loadList = scope.querySelector?.('.load_list[data-next-url]');
    if (loadList?.dataset?.nextUrl) return loadList.dataset.nextUrl;

    return '';
  }

  function markSearchContainerReady(container) {
    if (!container) return;
    container.setAttribute('data-shefa-search-infinite-ready', 'true');
  }

  function upgradeSearchPagination(root = document) {
    const searchRoot = root.matches?.('.template-search')
      ? root
      : root.querySelector?.('.template-search') || document.querySelector('.template-search');

    if (!searchRoot) return;

    const container = searchRoot.querySelector('#ProductGridContainer');
    if (!container) return;

    const target = container.querySelector(SEARCH_GRID_SELECTOR);
    if (!target) return;

    const existingSentinel = container.querySelector(SENTINEL_SELECTOR);
    if (existingSentinel) {
      markSearchContainerReady(container);
      return;
    }

    const nextUrl = getNextUrlFromScope(container);
    if (!nextUrl) {
      // There is no next page, but pagination can still be hidden because all results are present.
      markSearchContainerReady(container);
      return;
    }

    const sentinel = document.createElement('shefa-infinite-scroll');
    sentinel.className = 'shefa-infinite-scroll';
    sentinel.setAttribute('data-shefa-infinite-scroll', '');
    sentinel.dataset.target = SEARCH_GRID_SELECTOR;
    sentinel.dataset.nextUrl = nextUrl;
    sentinel.setAttribute('aria-live', 'polite');
    sentinel.innerHTML = `
      <span class="shefa-infinite-scroll__spinner" aria-hidden="true"></span>
      <span class="visually-hidden" data-shefa-infinite-scroll-status>Cargando más productos</span>
    `;

    // Keep it immediately after the product list so it appears before article/page results, if any.
    target.insertAdjacentElement('afterend', sentinel);
    markSearchContainerReady(container);
  }

  if (!customElements.get('shefa-infinite-scroll')) {
    class ShefaInfiniteScroll extends HTMLElement {
      constructor() {
        super();
        this.loading = false;
        this.controller = null;
        this.observer = null;
      }

      connectedCallback() {
        this.status = this.querySelector('[data-shefa-infinite-scroll-status]');
        const container = this.closest('#ProductGridContainer');
        if (container?.closest('.template-search')) markSearchContainerReady(container);
        this.observe();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.controller?.abort();
      }

      observe() {
        if (!this.dataset.nextUrl) {
          this.finish();
          return;
        }

        this.observer?.disconnect();
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.loadMore();
          },
          { rootMargin: '600px 0px' }
        );
        this.observer.observe(this);
      }

      async loadMore() {
        if (this.loading || !this.dataset.nextUrl || !this.isConnected) return;

        const container = this.closest('#ProductGridContainer');
        const selector = this.dataset.target;
        const target = container?.querySelector(selector);

        if (!container || !target) {
          this.finish();
          return;
        }

        this.loading = true;
        this.classList.add('is-loading');
        this.setStatus('Cargando más productos');
        this.controller = new AbortController();

        try {
          const nextUrl = new URL(this.dataset.nextUrl, window.location.origin);
          const response = await fetch(nextUrl.toString(), {
            signal: this.controller.signal,
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });

          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const html = await response.text();
          if (!this.isConnected) return;

          const doc = new DOMParser().parseFromString(html, 'text/html');
          const incomingTarget = doc.querySelector(selector);
          const incomingItems = incomingTarget
            ? Array.from(incomingTarget.children).filter((node) => node.matches?.('.grid__item'))
            : [];

          incomingItems.forEach((item) => {
            target.appendChild(document.importNode(item, true));
          });

          // Supports both the new Shefa sentinel and Loka's native pagination.
          const incomingContainer = doc.querySelector('#ProductGridContainer') || doc;
          const newNextUrl = getNextUrlFromScope(incomingContainer);

          if (newNextUrl) {
            this.dataset.nextUrl = newNextUrl;
            this.setStatus('Más productos disponibles');
          } else {
            this.finish();
            return;
          }
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.warn('[Shefa] No fue posible cargar la siguiente página de productos.', error);
            this.setStatus('No se pudieron cargar más productos. Desplázate para reintentar.');
          }
        } finally {
          this.loading = false;
          this.classList.remove('is-loading');
        }

        if (!this.isConnected || !this.dataset.nextUrl) return;

        requestAnimationFrame(() => {
          const rect = this.getBoundingClientRect();
          if (rect.top < window.innerHeight + 600) this.loadMore();
        });
      }

      setStatus(message) {
        if (this.status) this.status.textContent = message;
      }

      finish() {
        this.dataset.nextUrl = '';
        this.observer?.disconnect();
        this.classList.add('is-complete');
        this.setStatus('Todos los productos fueron cargados');
      }
    }

    customElements.define('shefa-infinite-scroll', ShefaInfiniteScroll);
  }

  const runUpgrade = () => requestAnimationFrame(() => upgradeSearchPagination(document));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runUpgrade, { once: true });
  } else {
    runUpgrade();
  }

  document.addEventListener('shopify:section:load', runUpgrade);

  // facets.js replaces ProductGridContainer after filtering/sorting.
  // Re-apply the search infinite-scroll behavior whenever that happens.
  const mutationObserver = new MutationObserver((mutations) => {
    if (
      mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === 1 &&
            (node.matches?.('#ProductGridContainer, .template-search') ||
              node.querySelector?.('#ProductGridContainer, .template-search'))
        )
      )
    ) {
      runUpgrade();
    }
  });

  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
